import React, { useState, useEffect, Fragment, useContext } from 'react';
import { Link, Redirect, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PlusTwoTone, TrashCanOutlineTwoTone, EditSquareOutlineTwoTone, ChevronUpTwoTone, ChevronDownTwoTone } from 'common/icons';
import { Category, AppActions } from 'common/types';
import { getTextIdentifier, setDocumentTitle, exportToJsonFile } from 'common/utils';
import { Routes, Entities } from 'common/enums';
import { Breadcrumbs } from 'common/components';
import { CommonLayout } from '../index';
import { appContext } from 'common/contexts';
import styles from './categoryList.module.css';

type CreateItemFormProps = {
  categories: Category[];
  actions: AppActions;
};

const CategoryList: React.FC<CreateItemFormProps> = ({ categories, actions }) => {
  const [error, setError] = useState<string | null>(null);
  const [redirect, setRedirect] = useState('');
  const { id } = useParams<{ id: string }>();
  const [t] = useTranslation();
  const [context] = useContext(appContext);

  useEffect(() => {
    const title = [t('admin.title'), t('admin.categories.title')];
    setDocumentTitle(title);
  }, [t]);

  // handle route params
  const selectedCategoryId = id || Entities.RootCategoryId;

  useEffect(() => {
    setRedirect('');
  }, [selectedCategoryId]);

  // handle redirect
  if (redirect) return <Redirect to={redirect} />;

  // redirect helper
  const updateRedirect = (categoryId: string | null) => {
    if (categoryId === selectedCategoryId) return;
    setRedirect(Routes.AdminCategoryList.replace(':id', categoryId ? categoryId : 'root'));
  };

  const handleOnChangeBreadCrumbs = (categoryId: string | null) => updateRedirect(categoryId);
  const handleOnClickOnCategoryListItem = (categoryId: string | null) => updateRedirect(categoryId);

  const handleOnClickOnCategoryListItemDelete = (event: React.MouseEvent, categoryId: string | null) => {
    event.stopPropagation();
    if (categoryId === null) return;
    
    // Verificar si hay ítems asociados a esta categoría
    const hasAssociatedItems = context.items.some(item => item.parentId === categoryId && !item.isDeleted);
    
    if (hasAssociatedItems) {
      setError(t('admin.categories.cannotDeleteWithItems', 'No se puede eliminar una categoría que tiene productos asociados'));
      // Ocultar el mensaje de error después de 3 segundos
      setTimeout(() => setError(null), 3000);
      return;
    }
    
    actions.category.remove(categoryId);
    setError(null);
  };

  const handleOnClickOnCategoryListItemEdit = (event: React.MouseEvent, categoryId: string | null) => {
    event.stopPropagation();
    setRedirect(Routes.AdminCategoryEdit.replace(':id', categoryId || 'root'));
  };

  const visibleCategories = categories
    .filter((entity) => !entity.isDeleted && !entity.isHidden && entity.parentId === selectedCategoryId)
    .sort((a, b) => {
      // Si ambas categorías tienen sortOrder, ordenar por sortOrder
      if (a.sortOrder !== undefined && b.sortOrder !== undefined) {
        return b.sortOrder - a.sortOrder; // Orden descendente
      }
      // Si no tienen sortOrder, ordenar por lastModifiedTime
      return (b.lastModifiedTime || 0) - (a.lastModifiedTime || 0);
    });

  const renderPresenter = (category: Category) => {
    const { picture, color, name } = category;
    const style = picture ? { backgroundImage: `url(${picture})` } : { backgroundColor: color || 'inherit' };
    return (
      <div className={styles.presenter} style={style}>
        {!category.picture && getTextIdentifier(name)}
      </div>
    );
  };

  // Manejar movimiento hacia arriba (aumentar sortOrder)
  const handleMoveUp = (event: React.MouseEvent, currentCategory: Category) => {
    event.preventDefault();
    event.stopPropagation();

    const currentIndex = visibleCategories.findIndex(cat => cat.id === currentCategory.id);

    // Si ya está en la primera posición o no se encuentra, no hacer nada
    if (currentIndex <= 0) return;

    // Obtener la categoría que está antes de la actual (con mayor sortOrder)
    const previousCategory = visibleCategories[currentIndex - 1];

    // Intercambiar sortOrder usando la acción swapSortOrder
    actions.category.swapSortOrder(currentCategory, previousCategory);
  };

  // Manejar movimiento hacia abajo (reducir sortOrder)
  const handleMoveDown = (event: React.MouseEvent, currentCategory: Category) => {
    event.preventDefault();
    event.stopPropagation();

    const currentIndex = visibleCategories.findIndex(cat => cat.id === currentCategory.id);

    // Si ya está en la última posición o no se encuentra, no hacer nada
    if (currentIndex === -1 || currentIndex >= visibleCategories.length - 1) return;

    // Obtener la categoría que está después de la actual (con menor sortOrder)
    const nextCategory = visibleCategories[currentIndex + 1];

    // Intercambiar sortOrder usando la acción swapSortOrder
    actions.category.swapSortOrder(currentCategory, nextCategory);
  };

  const renderCategory = (item: Category) => (
    <div key={`${item.id}`} className={styles.item} onClick={() => handleOnClickOnCategoryListItem(item.id)}>
      {renderPresenter(item)}
      <div className={styles.name}>{item.name.replace('Home Screen', t('common.homeScreen'))}</div>
      <div className={styles.publicStatus} title={item.isPublic ? t('admin.categories.isPublic') : t('admin.categories.isNotPublic')}>
        {/* Botones de ordenamiento */}
        <div className={styles.orderIconsContainer}>
          <div onClick={(evt: React.MouseEvent) => handleMoveUp(evt, item)} title={t('admin.categories.moveUp', 'Mover arriba')}>
            <ChevronUpTwoTone className={styles.orderIcon} />
          </div>
          <div onClick={(evt: React.MouseEvent) => handleMoveDown(evt, item)} title={t('admin.categories.moveDown', 'Mover abajo')}>
            <ChevronDownTwoTone className={styles.orderIcon} />
          </div>
        </div>
        <svg 
          className={`${styles.visibilityIcon} ${item.isPublic ? styles.visibilityIconActive : styles.visibilityIconInactive}`} 
          width="20" 
          height="20" 
          viewBox="0 0 24 24" 
          fill="none" 
          xmlns="http://www.w3.org/2000/svg"
        >
          {item.isPublic ? (
            // Ojo abierto (visible/público)
            <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
          ) : (
            // Ojo cerrado (invisible/privado)
            <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z" />
          )}
        </svg>
      </div>
      <button className="btn btn-link" onClick={(evt) => handleOnClickOnCategoryListItemEdit(evt, item.id)}>
        <EditSquareOutlineTwoTone />
      </button>
      <button className="btn btn-link" onClick={(evt) => handleOnClickOnCategoryListItemDelete(evt, item.id)}>
        <TrashCanOutlineTwoTone />
      </button>
    </div>
  );

  const renderHead = (
    <Fragment>
      <div className={styles.title}>{t('admin.categories.title')}</div>
      {error && <div className="alert alert-danger mt-2">{error}</div>}
      <Link className="btn btn-primary" to={Routes.AdminCategoryCreate.replace(':id', selectedCategoryId || 'root')}>
        <PlusTwoTone />
        <span>{t('admin.categories.addCategoryLabel')}</span>
      </Link>
      <button className="btn btn-outline-primary ml-2 mr-4" onClick={() => exportToJsonFile(categories)}>
        {t('common.exportToJSON')}
      </button>
    </Fragment>
  );

  const renderBody = (
    <Fragment>
      <Breadcrumbs
        className={styles.breadcrumbs}
        categories={categories}
        currentCategoryId={selectedCategoryId}
        onChange={handleOnChangeBreadCrumbs}
      />
      {visibleCategories.length ? (
        visibleCategories.map((item) => renderCategory(item))
      ) : (
        <div className={styles.empty}>{t('admin.categories.emptyListMessage')}</div>
      )}
    </Fragment>
  );

  return <CommonLayout head={renderHead} body={renderBody} />;
};

export default CategoryList;
